import { AxiosResponse } from 'axios'
import { APIHeaders } from '@ancon/wildcat-types'
import getEndpointWithVersion from '@ancon/wildcat-utils/api/getEndpointWithVersion'
import getUrlAndParams from '@ancon/wildcat-utils/api/getUrlAndParams'
import isErrorWithStatusCode from '@ancon/wildcat-utils/error/isErrorWithStatusCode'
import isHangfireResponse from '@ancon/wildcat-utils/hangfire/isHangfireResponse'
import isHangfireWorkerExpiredOrInvalidError from '@ancon/wildcat-utils/hangfire/isHangfireWorkerExpiredOrInvalidError'

import api from '../../../api'
import endpoints from '../../../api/endpoints/endpoints'
import hangfirePoller from '../../hangfire/utils/hangfirePoller'
import isHangfireWorkerLifetimeExceededError from '../../hangfire/utils/isHangfireWorkerLifetimeExceededError'
import {
  CheckOnlinePaymentAPIVersion,
  CheckOnlinePaymentWorkerLifetimeMillis,
} from '../constants'
import { CheckOnlinePaymentResult } from '../types'

/**
 * Ask the status endpoint once, after the poller gave up on the worker. Core's
 * own error, such as a 404 for a worker that is gone, says more than the
 * poller's, so it is thrown instead. Returns `null` while the worker runs.
 */
async function requestCheckOnlinePaymentStatus(
  params: { outletId: string; checkId: string; workerId: string },
  headers: APIHeaders,
  pollerError: unknown,
): Promise<CheckOnlinePaymentResult | null> {
  try {
    const response = await api.core.checks.get.onlinePaymentStatus(
      params,
      headers,
    )

    // Empty while the worker is still running
    return response.data || null
  } catch (error) {
    throw isErrorWithStatusCode(error) ? error : pollerError
  }
}

/**
 * Wait for the result of a v2.1 check online payment or payment action, from
 * the 202 response that started its worker.
 *
 * Errors of the status endpoint (400, 404, 423 and 500 when the job failed) are
 * thrown as they are. When the worker ran out of its lifetime or is gone, the
 * status endpoint is asked once more, and the poller error is thrown if that
 * has no result either.
 */
async function waitForCheckOnlinePaymentResult(
  response: AxiosResponse<unknown>,
  {
    outletId,
    checkId,
    headers,
  }: { outletId: string; checkId: string; headers: APIHeaders },
): Promise<CheckOnlinePaymentResult> {
  // Only a 202 starts a worker; any other answer already is the result
  if (!isHangfireResponse(response)) {
    return response.data as CheckOnlinePaymentResult
  }

  const workerId = response.data.id

  // Payments/Online and Payments/Online/Details share one status endpoint
  const { url } = getUrlAndParams(
    getEndpointWithVersion(
      endpoints.core.outlets.byId.checks.byId.payments.online.status.byId
        .details,
      CheckOnlinePaymentAPIVersion,
    ),
    { outletId, checkId, workerId },
  )

  try {
    const result = (await hangfirePoller.createWorker(checkId, {
      workerId,
      url: `${response.config.baseURL}/${url}`,
      headers,
      maxLifeTimeMillis: CheckOnlinePaymentWorkerLifetimeMillis,
    })) as AxiosResponse<CheckOnlinePaymentResult>

    return result.data
  } catch (error) {
    if (
      isHangfireWorkerLifetimeExceededError(error) ||
      isHangfireWorkerExpiredOrInvalidError(error)
    ) {
      const result = await requestCheckOnlinePaymentStatus(
        { outletId, checkId, workerId },
        headers,
        error,
      )

      if (result) {
        return result
      }
    }

    throw error
  }
}

export default waitForCheckOnlinePaymentResult
